import {
  IconDeviceFloppy,
  IconEdit,
  IconPin,
  IconPinFilled,
  IconRestore,
  IconTrash,
} from '@u21/tabler-icons';
import {
  U21Chip,
  U21Divider,
  U21Loading,
  U21MenuItem,
  U21MenuLayout,
} from 'app/shared/u21-ui/components';

import { Filter, FilterContext, SavedFilter } from 'app/modules/filters/models';
import { Dispatch, SetStateAction, useState } from 'react';
import { EditSavedFilterModal } from 'app/modules/filters/components/EditSavedFilterModal';
import { DeleteSavedFilterModal } from 'app/modules/filters/components/DeleteSavedFilterModal';
import { useUpdateSavedFilter } from 'app/modules/filters/queries/useUpdateSavedFilter';

interface Props {
  defaultFilters: Filter[];
  dirty: boolean;
  filterContext: FilterContext;
  filters: Filter[];
  onAppliedSavedFilterChange: Dispatch<SetStateAction<SavedFilter | undefined>>;
  onFiltersChange: (filters: Filter[]) => void;
  onPinnedSavedFilterIdsChange: Dispatch<SetStateAction<number[]>>;
  pinned: boolean;
  savedFilter: SavedFilter;
}

export const AppliedSavedFilterChip = ({
  defaultFilters,
  dirty,
  filterContext,
  filters,
  onAppliedSavedFilterChange,
  onFiltersChange,
  onPinnedSavedFilterIdsChange,
  pinned,
  savedFilter,
}: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const {
    mutateAsync: updateSavedFilter,
    isPending: isUpdateSavedFilterPending,
  } = useUpdateSavedFilter();
  return (
    <>
      <U21MenuLayout
        onClose={() => setMenuOpen(false)}
        trigger={
          <U21Chip
            color={dirty ? 'warning' : 'primary'}
            icon={pinned ? <IconPinFilled /> : undefined}
            onClick={() => setMenuOpen(true)}
            onDelete={() => {
              onAppliedSavedFilterChange(undefined);
              onFiltersChange(defaultFilters);
            }}
            rounded
            tooltip={
              dirty ? `Unsaved changes to ${savedFilter.title}` : undefined
            }
            variant="filled"
          >
            {savedFilter.title}
          </U21Chip>
        }
        open={menuOpen}
      >
        {dirty && (
          <>
            <U21MenuItem
              item={{
                disabled: isUpdateSavedFilterPending,
                icon: <IconDeviceFloppy />,
                rightIcon: (
                  <U21Loading
                    delay={0}
                    loading={isUpdateSavedFilterPending}
                    variant="spinner"
                  />
                ),
                text: 'Update filters',
                onClick: async () => {
                  try {
                    await updateSavedFilter({ id: savedFilter.id, filters });
                    setMenuOpen(false);
                  } catch {}
                },
              }}
            />
            <U21MenuItem
              item={{
                icon: <IconRestore />,
                text: 'Discard changes',
                onClick: () => {
                  onFiltersChange(savedFilter.filters);
                  setMenuOpen(false);
                },
              }}
            />
            <U21Divider horizontal />
          </>
        )}
        <U21MenuItem
          item={{
            icon: <IconPin />,
            text: pinned ? 'Unpin' : 'Pin',
            onClick: () => {
              if (pinned) {
                onPinnedSavedFilterIdsChange((prev) =>
                  prev.filter((i) => i !== savedFilter.id),
                );
              } else {
                onPinnedSavedFilterIdsChange((prev) => [
                  ...prev,
                  savedFilter.id,
                ]);
              }
              setMenuOpen(false);
            },
          }}
        />
        <U21Divider horizontal />
        <U21MenuItem
          item={{
            icon: <IconEdit />,
            text: 'Edit',
            onClick: () => {
              setEditOpen(true);
              setMenuOpen(false);
            },
          }}
        />
        <U21MenuItem
          item={{
            color: 'error',
            icon: <IconTrash />,
            text: 'Delete',
            onClick: () => {
              setDeleteOpen(true);
              setMenuOpen(false);
            },
          }}
        />
      </U21MenuLayout>
      <EditSavedFilterModal
        filters={filters}
        onClose={() => setEditOpen(false)}
        onUpdated={onAppliedSavedFilterChange}
        open={editOpen}
        savedFilter={savedFilter}
      />
      <DeleteSavedFilterModal
        filterContext={filterContext}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          onAppliedSavedFilterChange(undefined);
        }}
        open={deleteOpen}
        savedFilter={savedFilter}
      />
    </>
  );
};
